import { MesfModal } from "@dexteel/mesf-core";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	Alert,
	Button,
	CircularProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

type Props = {
	open: boolean;
	isSubmitting: boolean;
	copies?: string;
	onClose: () => void;
	onSubmit: (payload: { comments: string | null }) => void;
};

type FormValues = {
	comments: string;
};

export const NewVersionModal = ({
	open,
	isSubmitting,
	copies = "tests",
	onClose,
	onSubmit,
}: Props) => {
	const { control, handleSubmit, reset } = useForm<FormValues>({
		defaultValues: { comments: "" },
	});

	const submit: SubmitHandler<FormValues> = (data) => {
		onSubmit({
			comments: data.comments.trim() ? data.comments.trim() : null,
		});
	};

	useEffect(() => {
		if (open) {
			reset({ comments: "" });
		}
	}, [open]);

	return (
		<MesfModal
			open={open}
			handleClose={onClose}
			maxWidth="sm"
			title="New Draft"
		>
			<form
				data-test-id="new-version-modal"
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					handleSubmit(submit)(e);
				}}
			>
				<MesfModal.Content dividers>
					<Stack spacing={2.5} sx={{ py: 1 }}>
						<Alert
							severity="info"
							icon={<InfoOutlinedIcon fontSize="small" />}
							data-test-id="new-version-hint"
						>
							This draft copies the {copies} of the selected version. Revision
							number and effective date are set when it's confirmed.
						</Alert>

						<Stack spacing={0.75}>
							<Typography variant="subtitle2" fontWeight={600}>
								Comments{" "}
								<Typography
									component="span"
									variant="body2"
									color="text.secondary"
								>
									(optional)
								</Typography>
							</Typography>
							<Controller
								name="comments"
								control={control}
								render={({ field }) => (
									<TextField
										placeholder="Add context for this new draft..."
										fullWidth
										multiline
										minRows={4}
										value={field.value ?? ""}
										onChange={field.onChange}
										slotProps={{
											htmlInput: {
												"data-test-id": "new-version-comments-input",
											},
										}}
									/>
								)}
							/>
						</Stack>
					</Stack>
				</MesfModal.Content>
				<MesfModal.Actions sx={{ px: 2, py: 1.5, gap: 1 }}>
					<Button
						onClick={onClose}
						color="inherit"
						disabled={isSubmitting}
						data-test-id="cancel-new-version-button"
					>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="contained"
						disabled={isSubmitting}
						data-test-id="create-draft-button"
						startIcon={
							isSubmitting ? (
								<CircularProgress size={16} color="inherit" />
							) : undefined
						}
					>
						Create Draft
					</Button>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
